<template>
  <Transition name="consent-banner-slide">
    <div
      v-if="visible"
      class="fixed bottom-0 left-0 right-0 z-50 bg-input-border text-surface-dark py-4 md:py-3"
    >
      <div class="max-w-328 mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Main banner content -->
        <div class="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
          <div class="flex-1">
            <h4 class="h4 text-left">{{ $t('globals.cookie_warning_header') }}</h4>
            <p class="text-sm text-left">
              {{ warningText }}
              <a
                v-if="warningLink"
                :href="warningLink"
                target="_blank"
                rel="noopener noreferrer"
                class="underline hover:opacity-80 ml-1"
              >
                {{ $t('globals.cookie_warning_read_more') }}
              </a>
            </p>
          </div>

          <!-- Simple mode: just a dismiss button -->
          <div v-if="!hasTrackingConfigured" class="shrink-0">
            <button
              type="button"
              class="bg-surface-dark text-white font-semibold text-sm px-5 py-2 rounded-full cursor-pointer hover:opacity-90 transition-opacity"
              @click="dismissSimple"
            >
              {{ $t('globals.cookie_warning_dismiss') }}
            </button>
          </div>

          <!-- Granular consent mode: Accept All / Reject All / Customize -->
          <div v-else class="flex flex-wrap gap-2 shrink-0">
            <button
              type="button"
              class="bg-surface-dark text-white font-semibold text-sm px-5 py-2 rounded-full cursor-pointer hover:opacity-90 transition-opacity"
              @click="acceptAll"
            >
              {{ $t('globals.consent_accept_all') }}
            </button>
            <button
              type="button"
              class="bg-transparent border border-surface-dark text-surface-dark font-semibold text-sm px-5 py-2 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
              @click="rejectAll"
            >
              {{ $t('globals.consent_reject_all') }}
            </button>
            <button
              type="button"
              class="text-surface-dark font-semibold text-sm px-3 py-2 underline cursor-pointer hover:opacity-80 transition-opacity"
              @click="showPreferences = !showPreferences"
            >
              {{ $t('globals.consent_customize') }}
            </button>
          </div>
        </div>

        <!-- Expanded preferences panel (only in granular mode) -->
        <Transition name="preferences-expand">
          <div v-if="showPreferences && hasTrackingConfigured" class="mt-4 border-t border-surface-dark/20 pt-4">
            <div class="grid gap-3 md:grid-cols-3">
              <!-- Necessary (always on) -->
              <div class="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked
                  disabled
                  class="mt-1 h-4 w-4 rounded accent-surface-dark"
                />
                <div>
                  <p class="font-semibold text-sm">{{ $t('globals.consent_necessary_title') }}</p>
                  <p class="text-xs opacity-75">{{ $t('globals.consent_necessary_description') }}</p>
                </div>
              </div>

              <!-- Statistics -->
              <div class="flex items-start gap-3">
                <input
                  v-model="statisticsConsent"
                  type="checkbox"
                  class="mt-1 h-4 w-4 rounded accent-surface-dark cursor-pointer"
                />
                <div>
                  <p class="font-semibold text-sm">{{ $t('globals.consent_statistics_title') }}</p>
                  <p class="text-xs opacity-75">{{ $t('globals.consent_statistics_description') }}</p>
                </div>
              </div>

              <!-- Marketing -->
              <div class="flex items-start gap-3">
                <input
                  v-model="marketingConsent"
                  type="checkbox"
                  class="mt-1 h-4 w-4 rounded accent-surface-dark cursor-pointer"
                />
                <div>
                  <p class="font-semibold text-sm">{{ $t('globals.consent_marketing_title') }}</p>
                  <p class="text-xs opacity-75">{{ $t('globals.consent_marketing_description') }}</p>
                </div>
              </div>
            </div>

            <div class="mt-4 flex justify-end">
              <button
                type="button"
                class="bg-surface-dark text-white font-semibold text-sm px-5 py-2 rounded-full cursor-pointer hover:opacity-90 transition-opacity"
                @click="savePreferences"
              >
                {{ $t('globals.consent_save_preferences') }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';

import { useGeneralStore } from '@/stores/general';
import { getConsent, hasConsented, setConsent } from '@/utils/analytics/consent';
import { isEmbed } from '@/utils/isEmbed';

const { locale } = useI18n();
const generalStore = useGeneralStore();

const SIMPLE_STORAGE_KEY = 'SFT_cookie_warning_expire';
const SIMPLE_DISMISS_DAYS = 365;

const showPreferences = ref(false);
const statisticsConsent = ref(false);
const marketingConsent = ref(false);
const dismissed = ref(false);
const forceShow = ref(false);

// Determine if any tracking service is configured
const hasTrackingConfigured = computed(() => {
  const customer = generalStore.customer();
  return !!(
    customer.google_analytics_trackingid_ng ||
    customer.google_tag_manager_trackingid ||
    (customer.fb_pixel_events_id && customer.fb_pixel_events)
  );
});

// Check if simple banner was previously dismissed (old logic)
const simplePreviouslyDismissed = (() => {
  try {
    const stored = localStorage.getItem(SIMPLE_STORAGE_KEY);
    if (stored != null && Number(stored) > Date.now()) {
      return true;
    }
  } catch {
    // localStorage may be unavailable
  }
  return false;
})();

// Load existing preferences if any
const existing = getConsent();
if (existing) {
  statisticsConsent.value = existing.statistics;
  marketingConsent.value = existing.marketing;
}

const cookieWarningForLocale = computed(() => {
  const cw = generalStore.cookieWarning();
  const lang = locale.value;
  if (cw[lang] && cw[lang].cookie_warning_on) {
    return cw[lang];
  }
  return null;
});

const legacyWarning = computed(() => {
  const customer = generalStore.customer();
  if (customer.cookie_warning && customer.cookie_warning_text) {
    return {
      text: customer.cookie_warning_text,
      link: customer.cookie_warning_link,
    };
  }
  return null;
});

const warningText = computed(() => {
  return cookieWarningForLocale.value?.cookie_warning_text ?? legacyWarning.value?.text ?? '';
});

const warningLink = computed(() => {
  return cookieWarningForLocale.value?.cookie_warning_link ?? legacyWarning.value?.link ?? '';
});

const shouldShow = computed(() => {
  if (isEmbed()) return false;
  if (dismissed.value) return false;
  if (forceShow.value) return true;

  // Must have cookie warning config to show at all
  if (cookieWarningForLocale.value == null && legacyWarning.value == null) return false;

  // Simple mode: check old localStorage key
  if (!hasTrackingConfigured.value) {
    return !simplePreviouslyDismissed;
  }

  // Granular mode: check consent preferences
  return !hasConsented();
});

const visible = ref(false);

watchEffect(() => {
  visible.value = shouldShow.value;
});

function dismissSimple() {
  visible.value = false;
  dismissed.value = true;
  try {
    const expiry = Date.now() + SIMPLE_DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(SIMPLE_STORAGE_KEY, String(expiry));
  } catch {
    // localStorage may be unavailable
  }
}

function acceptAll() {
  statisticsConsent.value = true;
  marketingConsent.value = true;
  setConsent({ statistics: true, marketing: true });
  close();
}

function rejectAll() {
  statisticsConsent.value = false;
  marketingConsent.value = false;
  setConsent({ statistics: false, marketing: false });
  close();
}

function savePreferences() {
  setConsent({
    statistics: statisticsConsent.value,
    marketing: marketingConsent.value,
  });
  close();
}

function close() {
  visible.value = false;
  dismissed.value = true;
  forceShow.value = false;
  showPreferences.value = false;
}

/**
 * Reopen the consent banner (called from footer link).
 */
function reopen() {
  dismissed.value = false;
  forceShow.value = true;
  showPreferences.value = true;
  // Reload stored preferences
  const stored = getConsent();
  if (stored) {
    statisticsConsent.value = stored.statistics;
    marketingConsent.value = stored.marketing;
  }
}

// Expose reopen for external usage (e.g., footer link)
defineExpose({ reopen });

// Also listen for a global event to reopen (for use from the footer)
window.addEventListener('sft-open-consent-settings', reopen);
</script>

<style scoped>
.consent-banner-slide-enter-active,
.consent-banner-slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.consent-banner-slide-enter-from {
  transform: translateY(100%);
  opacity: 0;
}
.consent-banner-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.preferences-expand-enter-active,
.preferences-expand-leave-active {
  transition: max-height 0.3s ease, opacity 0.2s ease;
  overflow: hidden;
}
.preferences-expand-enter-from,
.preferences-expand-leave-to {
  max-height: 0;
  opacity: 0;
}
.preferences-expand-enter-to,
.preferences-expand-leave-from {
  max-height: 300px;
  opacity: 1;
}
</style>
